<template>
    <Modal class="add-gocardless-bank-account-modal"
           icon="fa-solid fa-building-columns"
           title="PAYMENT.BANK_ACCOUNT_DETAILS">
        <ErrorMessage v-if="error"
                      :error="error" />

        <form v-if="stage === 'form'"
              class="form-h"
              @keydown.enter="formOk">
            <BankAccountForm v-model="internalBankAccount" />
        </form>
        <div v-else-if="stage === 'confirm'">
            <p class="text-center">
                {{ l('GOCARDLESS.CONFIRM_GUARANTEE') }}
            </p>

            <div class="well"
                 v-html="directDebitGuaranteeText">
            </div>
        </div>

        <template v-slot:footer>
            <div class="modl-footer">
                <!-- Cancel Button -->
                <SquareBtn class="cancel-btn modl__cancel-btn"
                           color="default"
                           size="lg"
                           @click="cancel">
                    {{ l('CANCEL') }}
                </SquareBtn>

                <!-- Buttons when form is shown -->
                <template v-if="stage === 'form'">
                    <SquareBtn class="continue-btn"
                               color="info"
                               size="lg"
                               @click="formOk">
                        {{ l('CONTINUE') }}
                    </SquareBtn>
                </template>
                <!-- Buttons when confirmation is shown -->
                <template v-else-if="stage === 'confirm'">
                    <SquareBtn class="print-guarantee-btn"
                               color="info"
                               size="lg"
                               @click="printGuarantee">
                        {{ l('GOCARDLESS.PRINT_GUARANTEE') }}
                    </SquareBtn>

                    <SquareBtn class="ok-btn confirm-btn"
                               color="primary"
                               size="lg"
                               :loading="submitting"
                               @click="confirmOk">
                        {{ l('CONFIRM') }}
                    </SquareBtn>
                </template>
            </div>
        </template>
    </Modal>
</template>

<script>
import BankAccount from '@/lib/PaymentMethods/GoCardless/BankAccount';
import BankAccountForm from '@/epos/vue/components/Payment/BankAccountForm';
import directDebitGuaranteeText from '@/lib/PaymentMethods/GoCardless/directDebitGuaranteeText';
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import Modal from '@/vue/components/Modals/Modal';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { validateRequiredFields } from '@/functions/forms';

export default {
    components: {
        SquareBtn,
        BankAccountForm,
        ErrorMessage,
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        /**
         * @type {CustomerAccount}
         */
        customerAccount: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            internalBankAccount: new BankAccount({
                accountHolderFirstName: this.customerAccount.primaryContact?.firstName || '',
                accountHolderLastName: this.customerAccount.primaryContact?.lastName || '',
                accountHolderEmail: this.customerAccount.primaryContact?.email || '',
                accountNumber: '',
                branchCode: '',
            }),

            /**
             * @type {?Error}
             */
            error: null,

            /**
             * @type {boolean}
             */
            submitting: false,

            /**
             * @type {"form"|"confirm"}
             */
            stage: 'form',
        };
    },

    computed: {
        /**
         * @return {string}
         */
        directDebitGuaranteeText() {
            return directDebitGuaranteeText(container.userService.company.name);
        },
    },

    methods: {
        formOk() {
            if (this.validateFormData()) {
                this.stage = 'confirm';
            }
        },

        getFormData() {
            return {
                accountHolderFirstName: this.internalBankAccount.accountHolderFirstName,
                accountHolderLastName: this.internalBankAccount.accountHolderLastName,
                accountHolderEmail: this.internalBankAccount.accountHolderEmail,
                accountNumber: this.internalBankAccount.accountNumber,
                branchCode: this.internalBankAccount.branchCode,
                countryCode: this.internalBankAccount.countryCode,
            };
        },

        validateFormData() {
            try {
                validateRequiredFields(
                    {
                        accountHolderFirstName: 'PAYMENT.BANK_ACCOUNT_FIRST_NAME',
                        accountHolderLastName: 'PAYMENT.BANK_ACCOUNT_LAST_NAME',
                        accountHolderEmail: 'CUSTOMER_DETAILS.EMAIL',
                        accountNumber: 'PAYMENT.BANK_ACCOUNT_NUMBER',
                        branchCode: 'PAYMENT.BANK_ACCOUNT_SORT_CODE',
                    },
                    this.getFormData(),
                );
                return true;
            } catch (err) {
                this.error = err;
                // Go back to the form on error.
                this.stage = 'form';
                return false;
            }
        },

        async confirmOk() {
            this.error = null;
            this.submitting = true;
            try {
                const { data } = await container.apiClient.request(
                    'POST',
                    `customeraccounts/${this.customerAccount.ID}/gocardless/mandates`,
                    this.getFormData(),
                );
                if (!data.paymentToken) {
                    throw new Error('API response did not include a payment token.');
                }

                this.resolve(data.paymentToken);
            } catch (err) {
                this.error = err;
                // Go back to the form on error.
                this.stage = 'form';
            } finally {
                this.submitting = false;
            }
        },

        printGuarantee() {
            container.receiptPrinter.printDirectDebitGuarantee();
        },
    },
};
</script>

<style lang="less">
.add-gocardless-bank-account-modal {
    .direct-debit-guarantee {
        img {
            width: 200px;
            margin-bottom: 15px;
        }

        h1 {
            font-size: 16px;
            margin-bottom: 15px;
            font-weight: bold;
        }

        p {
            margin-bottom: 10px;
        }
    }
}
</style>
